import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";
import { Cog, ShieldCheck, Sparkles } from "lucide-react";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import type { HookPoint, Plugin } from "../../types";
import { getBindingDisplayInfo, getConfiguredEndpoints } from "../../utils";
import type { PluginRow, PluginTableMeta } from "./connected-systems-section";
import { SystemActions } from "./system-actions";

// Column header with tooltip
type ColumnHeaderWithTooltipProps = {
  column: Parameters<
    Exclude<ColumnDef<PluginRow>["header"], string | undefined>
  >[0]["column"];
  title: string;
  tooltip: string;
};

const ColumnHeaderWithTooltip = ({
  column,
  title,
  tooltip,
}: ColumnHeaderWithTooltipProps) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger render={<div className="inline-flex" />}>
        <DataTableColumnHeader column={column} title={title} />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Hook point config for endpoints column — colors match pipeline stage accents
const HOOK_POINT_LABELS: Record<
  HookPoint,
  {
    icon: typeof ShieldCheck;
    label: string;
    shortLabel: string;
    enabledClass: string;
    disabledClass: string;
  }
> = {
  "tool.access": {
    icon: ShieldCheck,
    label: "Access",
    shortLabel: "Access",
    enabledClass: "border-amber-500/50 bg-amber-500/10 text-amber-600",
    disabledClass: "border-muted bg-muted/30 text-muted-foreground/40",
  },
  "tool.pre": {
    icon: Cog,
    label: "Pre tool execution",
    shortLabel: "Pre",
    enabledClass: "border-blue-500/50 bg-blue-500/10 text-blue-600",
    disabledClass: "border-muted bg-muted/30 text-muted-foreground/40",
  },
  "tool.post": {
    icon: Sparkles,
    label: "Post tool execution",
    shortLabel: "Post",
    enabledClass: "border-purple-500/50 bg-purple-500/10 text-purple-600",
    disabledClass: "border-muted bg-muted/30 text-muted-foreground/40",
  },
};

// Name cell component with hover and click functionality
const NameCell = ({
  plugin,
  onHoverPlugin,
  onSelectPlugin,
}: {
  plugin: Plugin;
  onHoverPlugin?: (pluginId: string | null) => void;
  onSelectPlugin?: (pluginId: string) => void;
}) => (
  <button
    className="flex cursor-pointer flex-col rounded-sm px-1 py-0.5 transition-colors hover:bg-muted/50"
    onBlur={() => onHoverPlugin?.(null)}
    onClick={() => onSelectPlugin?.(plugin.id)}
    onFocus={() => onHoverPlugin?.(plugin.id)}
    onPointerEnter={() => onHoverPlugin?.(plugin.id)}
    onPointerLeave={() => onHoverPlugin?.(null)}
    type="button"
  >
    <span className="font-medium text-foreground underline-offset-2 hover:underline">
      {plugin.name}
    </span>
    {!!plugin.description && (
      <span className="max-w-xs truncate text-muted-foreground text-xs">
        {plugin.description}
      </span>
    )}
  </button>
);

// Plugin type display mapping
const PLUGIN_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  webhook: { label: "Webhook", icon: "🔗" },
};

export const systemColumns: ColumnDef<PluginRow>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <ColumnHeaderWithTooltip
        column={column}
        title="Name"
        tooltip="The display name of the connected system"
      />
    ),
    cell: ({ row, table }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const meta = table.options.meta as PluginTableMeta | undefined;
      return (
        <NameCell
          onHoverPlugin={meta?.onHoverPlugin}
          onSelectPlugin={meta?.onSelectPlugin}
          plugin={row.original}
        />
      );
    },
    filterFn: (row, _id, value) => {
      const name = row.original.name?.toLowerCase() || "";
      const description = row.original.description?.toLowerCase() || "";
      const searchValue = value.toLowerCase();
      return name.includes(searchValue) || description.includes(searchValue);
    },
  },
  {
    id: "type",
    accessorKey: "plugin_type",
    header: ({ column }) => (
      <ColumnHeaderWithTooltip
        column={column}
        title="Type"
        tooltip="How the system connects to Arcade (e.g., Webhook for HTTP endpoints)"
      />
    ),
    cell: ({ row }) => {
      const pluginType = row.original.plugin_type;
      const typeInfo = PLUGIN_TYPE_LABELS[pluginType] ?? {
        label: pluginType,
        icon: "⚙️",
      };
      return (
        <div className="flex items-center gap-1.5">
          <span>{typeInfo.icon}</span>
          <span className="text-sm">{typeInfo.label}</span>
        </div>
      );
    },
  },
  {
    id: "scope",
    accessorKey: "binding.type",
    header: ({ column }) => (
      <ColumnHeaderWithTooltip
        column={column}
        title="Scope"
        tooltip="Organization-scoped systems apply to all projects. Project-scoped systems apply only to this project."
      />
    ),
    cell: ({ row }) => {
      const bindingType = row.original.binding.type;
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const info = getBindingDisplayInfo(bindingType as "tenant" | "project");
      return (
        <Badge className={info.colorClass} variant="outline">
          {info.label}
        </Badge>
      );
    },
  },
  {
    id: "endpoints",
    header: ({ column }) => (
      <ColumnHeaderWithTooltip
        column={column}
        title="Endpoints"
        tooltip="Which hook points this system has configured: Access (tool visibility), Pre (before execution), Post (after execution)"
      />
    ),
    cell: ({ row }) => {
      const pluginHooks = row.original._pluginHooks;
      const configuredEndpoints = getConfiguredEndpoints(row.original);
      const allHookPoints: HookPoint[] = [
        "tool.access",
        "tool.pre",
        "tool.post",
      ];

      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {allHookPoints.map((hookPoint) => {
            const config = HOOK_POINT_LABELS[hookPoint];
            const isConfigured = configuredEndpoints.includes(hookPoint);
            const hook = pluginHooks.find((h) => h.hook_point === hookPoint);
            const isEnabled = isConfigured && hook?.status === "active";

            return (
              <TooltipProvider key={hookPoint}>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        className={cn(
                          "px-2 py-0.5 font-medium text-[11px] transition-colors",
                          isEnabled ? config.enabledClass : config.disabledClass
                        )}
                        variant="outline"
                      />
                    }
                  >
                    {config.shortLabel}
                  </TooltipTrigger>
                  <TooltipContent>
                    {`${config.label}${isEnabled ? " (enabled)" : " (disabled)"}`}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const binding = row.original.binding.type as "tenant" | "project";
      return <SystemActions binding={binding} plugin={row.original} />;
    },
  },
];
